/**
 * ChainVisualizationAssembler - Constructor/Factory for dynamic chain visualization configuration
 *
 * This assembler analyzes the chain topology data and constructs optimal grid configurations
 * for the 3-layer visualization system, taking into account:
 * - Number of topology groups/subsections
 * - Step positions and types for each track
 * - Individual row heights based on stacked steps
 * - Dynamic color palette generation
 * - Grid synchronization parameters
 */

// Color manipulation utilities

/**
 * Generates a sophisticated color palette based on the user's implementation color
 * Creates softer versions, contrasting colors, and incorporates grays
 * @param {string} baseColor - User's implementation_color (hex format)
 * @param {number} count - Number of colors needed
 * @returns {Array<string>} Array of hex colors
 */
const generateDynamicColorPalette = (baseColor, count) => {
  // If no base color provided, fall back to curated palette with grays
  if (!baseColor) {
    return [
      "#6366f1", // Primary blue-purple
      "#8b5cf6", // Soft purple
      "#06b6d4", // Teal
      "#84cc16", // Lime green
      "#f59e0b", // Amber
      "#ef4444", // Red
      "#64748b", // Slate gray
      "#9ca3af", // Cool gray
    ].slice(0, count);
  }

  // Color utility functions
  const hexToHsl = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h,
      s,
      l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          h = 0;
      }
      h /= 6;
    }
    return [h, s, l];
  };

  const hslToHex = (h, s, l) => {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const r = hue2rgb(p, q, h + 1 / 3);
    const g = hue2rgb(p, q, h);
    const b = hue2rgb(p, q, h - 1 / 3);

    const toHex = (c) => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const [baseH, baseS, baseL] = hexToHsl(baseColor);
  const colors = [];

  // Create a monochromatic palette strategy with subtle contrast
  const paletteTypes = [
    // 1. Base color (original)
    { h: baseH, s: Math.min(baseS * 0.9, 0.8), l: Math.max(baseL, 0.45) },

    // 2. Darker shade of base color
    { h: baseH, s: Math.min(baseS * 0.8, 0.7), l: Math.max(baseL - 0.15, 0.3) },

    // 3. Lighter tint of base color
    { h: baseH, s: Math.min(baseS * 0.6, 0.5), l: Math.min(baseL + 0.2, 0.75) },

    // 4. Desaturated base color
    { h: baseH, s: Math.min(baseS * 0.4, 0.3), l: Math.max(baseL, 0.5) },

    // 5. Complementary color (subtle contrast)
    {
      h: (baseH + 0.5) % 1,
      s: Math.min(baseS * 0.5, 0.4),
      l: Math.max(baseL + 0.1, 0.55),
    },

    // 6. Darker complementary shade
    {
      h: (baseH + 0.5) % 1,
      s: Math.min(baseS * 0.6, 0.5),
      l: Math.max(baseL - 0.1, 0.35),
    },

    // 7. Neutral with base hue influence
    { h: baseH, s: 0.15, l: 0.6 },

    // 8. Very light base tint (for backgrounds)
    { h: baseH, s: Math.min(baseS * 0.3, 0.2), l: 0.8 },
  ];

  // Generate colors based on palette types, cycling if needed
  for (let i = 0; i < count; i++) {
    const paletteType = paletteTypes[i % paletteTypes.length];

    // Add slight variation for repeated colors
    const variation = Math.floor(i / paletteTypes.length);
    const hueVariation = variation * 0.05;
    const lightnessVariation = variation * 0.1;

    const finalH = (paletteType.h + hueVariation) % 1;
    const finalS = Math.max(0.05, Math.min(0.8, paletteType.s));
    const finalL = Math.max(
      0.2,
      Math.min(0.8, paletteType.l + lightnessVariation),
    );

    colors.push(hslToHex(finalH, finalS, finalL));
  }

  return colors;
};

/**
 * Analyzes step data to determine stacked steps per column for a track
 * @param {Array} steps - Array of step objects with type property
 * @returns {Object} Object with step types as keys and stack counts as values
 */
const analyzeStepStacking = (steps) => {
  const stepGroups = {};

  steps.forEach((step) => {
    if (!stepGroups[step.type]) {
      stepGroups[step.type] = [];
    }
    stepGroups[step.type].push(step);
  });

  return stepGroups;
};

/**
 * Calculates individual row height based on maximum stacked steps in that track
 * @param {Object} track - Track object with steps array
 * @param {number} baseRowHeight - Base height for single step
 * @returns {number} Calculated row height in pixels
 */
const calculateTrackRowHeight = (track, baseRowHeight = 40) => {
  const stepGroups = analyzeStepStacking(track.steps);
  const maxStackedSteps = Math.max(
    ...Object.values(stepGroups).map((group) => group.length),
    1, // Minimum 1
  );

  return baseRowHeight * maxStackedSteps;
};

/**
 * Determines the optimal number of columns based on step types present in data
 * @param {Object} data - Chain visualization data
 * @returns {number} Number of columns needed
 */
const calculateColumnCount = (data) => {
  const stepTypes = new Set();

  data.topologySubsections.forEach((subsection) => {
    subsection.tracks.forEach((track) => {
      track.steps.forEach((step) => {
        stepTypes.add(step.type);
      });
    });
  });

  // Base columns: PriceList + TrackLabel + step columns
  // Step types: step1, step2, step3, step5 -> 4 step columns (removed step4 for margins)
  const stepColumns = Math.max(
    ...Array.from(stepTypes)
      .filter((type) => type !== "step4") // Filter out step4 (margins column)
      .map((type) => parseInt(type.replace("step", "")) || 0),
  );

  // Adjust for removed step4: if step5 exists, it should be in position 4 (not 5)
  const hasStep5 = stepTypes.has("step5");
  const adjustedStepColumns = hasStep5 ? 4 : Math.min(stepColumns, 3);

  return 2 + adjustedStepColumns; // PriceList(1) + TrackLabel(1) + step columns
};

/**
 * Generates responsive column template based on screen size and number of step columns
 * @param {number} totalColumns - Total number of columns
 * @param {Object} options - Responsive options
 * @returns {Object} CSS Grid template columns with responsive variants
 */
const generateColumnTemplate = (totalColumns) => {
  // Use a single template for all screen sizes, with reduced track label size for better text wrapping
  const priceListSize = "0.5fr";
  const trackLabelSize = "0.6fr"; // Reduced for better text wrapping
  const step1ColumnSize = "0.65fr"; // Half size of the original 1.3fr
  const stepColumnSize = "1fr";

  let template = `${priceListSize} ${step1ColumnSize} ${trackLabelSize}`;
  const stepColumns = totalColumns - 2; // Subtract priceList and trackLabel columns
  for (let i = 1; i < stepColumns; i++) {
    // Start from 1 since step1 already handled
    template += ` ${stepColumnSize}`;
  }

  return template;
};

/**
 * Adjusts sellout steps (step5) to match the count of the last channel step (step2 or step3)
 * @param {Object} data - Chain visualization data
 * @returns {Object} Modified data with adjusted sellout steps
 */
const adjustSelloutStepsToMatchChannels = (data) => {
  const modifiedData = JSON.parse(JSON.stringify(data)); // Deep clone

  modifiedData.topologySubsections.forEach((subsection) => {
    subsection.tracks.forEach((track) => {
      // Find the last channel step (step2 or step3)
      const channelSteps = track.steps.filter(
        (step) => step.type === "step2" || step.type === "step3",
      );

      // Determine the last channel step
      const lastChannelStep =
        channelSteps.length > 0 ? channelSteps[channelSteps.length - 1] : null;

      if (lastChannelStep) {
        // Find step5 (sellout) steps
        const selloutSteps = track.steps.filter(
          (step) => step.type === "step5",
        );

        if (selloutSteps.length > 0) {
          // Determine how many instances the last channel step should have
          let targetCount = 1;

          // If the last channel step has stacked data, count it
          if (
            lastChannelStep.node2_reference_client &&
            Array.isArray(lastChannelStep.node2_reference_client)
          ) {
            targetCount = Math.max(
              targetCount,
              lastChannelStep.node2_reference_client.length,
            );
          }
          if (
            lastChannelStep.node3_reference_pos &&
            Array.isArray(lastChannelStep.node3_reference_pos)
          ) {
            targetCount = Math.max(
              targetCount,
              lastChannelStep.node3_reference_pos.length,
            );
          }

          // Count channel steps by type to determine stacking
          const step2Count = track.steps.filter(
            (s) => s.type === "step2",
          ).length;
          const step3Count = track.steps.filter(
            (s) => s.type === "step3",
          ).length;

          // Use the maximum count from channel steps
          if (lastChannelStep.type === "step2" && step2Count > 1) {
            targetCount = Math.max(targetCount, step2Count);
          } else if (lastChannelStep.type === "step3" && step3Count > 1) {
            targetCount = Math.max(targetCount, step3Count);
          }

          // Adjust sellout steps to match target count
          const currentSelloutCount = selloutSteps.length;

          if (currentSelloutCount < targetCount) {
            // Add more sellout steps
            for (let i = currentSelloutCount; i < targetCount; i++) {
              track.steps.push({
                type: "step5",
                // Copy data from the first sellout step if available
                ...(selloutSteps[0] && typeof selloutSteps[0] === "object"
                  ? JSON.parse(JSON.stringify(selloutSteps[0]))
                  : {}),
              });
            }
          } else if (currentSelloutCount > targetCount) {
            // Remove excess sellout steps
            const step5Indices = [];
            track.steps.forEach((step, index) => {
              if (step.type === "step5") {
                step5Indices.push(index);
              }
            });

            // Remove from the end
            const toRemove = currentSelloutCount - targetCount;
            for (let i = 0; i < toRemove; i++) {
              const indexToRemove = step5Indices.pop();
              track.steps.splice(indexToRemove, 1);
            }
          }
        }
      }
    });
  });

  return modifiedData;
};

/**
 * Processes chain data from the new simplified backend response structure
 * @param {Object} responseData - Raw response from backend
 * @returns {Object} Processed chain data ready for visualization
 */
export const assembleChainVisualizationData = (responseData) => {
  if (!responseData || !responseData.topologySubsections) {
    return getEmptyChainStructure();
  }

  // Backend now returns clean structure - no need for complex transformation
  return {
    priceList: responseData.priceList ?? null,
    sections: responseData.sections || ["Production", "Distribution", "Retail"],
    gridConfig: responseData.gridConfig || {
      maxBoxesPerStep: 1,
      baseRowHeight: 40,
      gapBetweenSubsections: 20,
    },
    topologySubsections: responseData.topologySubsections || [],
  };
};

const getEmptyChainStructure = () => ({
  priceList: null,
  sections: [],
  gridConfig: {
    maxBoxesPerStep: 1,
    baseRowHeight: 40,
    gapBetweenSubsections: 20,
  },
  topologySubsections: [],
});

/**
 * Main assembler function that constructs the complete configuration
 * @param {Object} chainData - Raw chain topology data
 * @param {string} userImplementationColor - User's implementation color
 * @param {Object} options - Optional configuration overrides
 * @returns {Object} Complete configuration for ChainVisualization
 */
export const assembleChainVisualizationConfig = (
  chainData,
  userImplementationColor,
  options = {},
) => {
  // Input validation
  if (!chainData || !chainData.topologySubsections) {
    throw new Error("Invalid chain data: missing topologySubsections");
  }

  // Process chain data to ensure compatibility
  const processedChainData = assembleChainVisualizationData(chainData);

  // Adjust sellout steps to match channel steps before processing
  const adjustedChainData =
    adjustSelloutStepsToMatchChannels(processedChainData);

  const {
    baseRowHeight = 40,
    minRowHeight = 40,
    maxRowHeight = 200,
    headerHeight = 30,
    padding = 32,
    gapBetweenSubsections = 12,
  } = options;

  // Analyze data structure
  const totalSubsections = adjustedChainData.topologySubsections.length;
  const totalTracks = adjustedChainData.topologySubsections.reduce(
    (sum, subsection) => sum + subsection.tracks.length,
    0,
  );

  const totalColumns = calculateColumnCount(adjustedChainData);
  const columnTemplates = generateColumnTemplate(totalColumns);

  // Calculate individual row heights and positions with subsection gaps
  const rowConfigurations = [];
  const gapConfigurations = [];
  let currentRowIndex = 0;

  adjustedChainData.topologySubsections.forEach(
    (subsection, subsectionIndex) => {
      subsection.tracks.forEach((track, trackIndex) => {
        const trackRowHeight = Math.min(
          Math.max(calculateTrackRowHeight(track, baseRowHeight), minRowHeight),
          maxRowHeight,
        );

        const stepGroups = analyzeStepStacking(track.steps);

        rowConfigurations.push({
          subsectionName: subsection.name,
          subsectionIndex,
          trackIndex,
          trackId: track.id,
          trackLabel: track.label,
          row: currentRowIndex + 1, // CSS Grid is 1-indexed
          height: trackRowHeight,
          isFirstInSubsection: trackIndex === 0,
          isLastInSubsection: trackIndex === subsection.tracks.length - 1,
          stepGroups,
          maxStackedSteps: Math.max(
            ...Object.values(stepGroups).map((group) => group.length),
            1,
          ),
        });

        currentRowIndex++;
      });

      // Add gap row after each subsection (except the last one)
      if (subsectionIndex < adjustedChainData.topologySubsections.length - 1) {
        gapConfigurations.push({
          type: "subsectionGap",
          subsectionIndex,
          row: currentRowIndex + 1,
          height: gapBetweenSubsections,
          isGapAfterSubsection: subsection.name,
        });
        currentRowIndex++;
      }
    },
  );

  // Generate dynamic color palette
  const colorPalette = generateDynamicColorPalette(
    userImplementationColor,
    totalSubsections,
  );

  // Create grid template with individual row heights and gaps
  const allRowConfigs = [...rowConfigurations, ...gapConfigurations].sort(
    (a, b) => a.row - b.row,
  );

  const rowTemplate = allRowConfigs
    .map((config) => `${config.height}px`)
    .join(" ");

  // Calculate total height including gaps
  const totalContentHeight = allRowConfigs.reduce(
    (sum, config) => sum + config.height,
    0,
  );
  const totalHeight = headerHeight + totalContentHeight + padding;

  // Calculate scrollable content height (without header, for PriceListBox height matching)
  const scrollableContentHeight = totalContentHeight + 24; // Add padding from scrollableOverlay (12px top + 12px bottom)

  // Calculate dynamic positioning for arrows and labels
  const calculateColumnPositions = (templateString = columnTemplates) => {
    const positions = {};
    const columnSizes = templateString.split(" ");
    let cumulativeSize = 0;
    let totalFr = 0;

    // Calculate total fr units
    columnSizes.forEach((size) => {
      if (size.endsWith("fr")) {
        totalFr += parseFloat(size);
      }
    });

    columnSizes.forEach((size, index) => {
      const columnKey = [
        "priceList",
        "step1",
        "trackLabel",
        "step2",
        "step3",
        // "step4", // REMOVED for margins column elimination
        "step5",
      ][index];
      if (columnKey) {
        positions[columnKey] = {
          start: (cumulativeSize / totalFr) * 100,
          size: (parseFloat(size) / totalFr) * 100,
        };
        positions[columnKey].end =
          positions[columnKey].start + positions[columnKey].size;
        cumulativeSize += parseFloat(size);
      }
    });

    return positions;
  };

  const columnPositions = calculateColumnPositions();

  // Generate group labels configuration for topology subsections
  const groupLabels = rowConfigurations
    .filter((config) => config.isFirstInSubsection)
    .map((config) => ({
      subsectionName: config.subsectionName,
      subsectionIndex: config.subsectionIndex,
      row: config.row,
      color: colorPalette[config.subsectionIndex % colorPalette.length],
      trackId: config.trackId,
    }));

  // Final configuration object
  return {
    // Data references
    data: adjustedChainData,
    userImplementationColor,

    // Grid structure
    grid: {
      rows: totalTracks,
      columns: totalColumns,
      template: {
        rows: rowTemplate,
        columns: columnTemplates,
      },
      rowConfigurations,
      gapConfigurations,
      columnPositions,
      groupLabels, // Add group labels to grid configuration
    },

    // Visual configuration
    visual: {
      colors: colorPalette,
      baseRowHeight,
      headerHeight,
      padding,
      totalHeight,
      gapBetweenSubsections,
      scrollableContentHeight, // Height of scrollable content area for PriceListBox matching
    },

    // Calculated metrics
    metrics: {
      totalSubsections,
      totalTracks,
      totalColumns,
      maxStepsInAnyTrack: Math.max(
        ...rowConfigurations.map((config) => config.maxStackedSteps),
      ),
      averageRowHeight: totalContentHeight / totalTracks,
    },

    // Helper functions for components
    helpers: {
      getColorForSubsection: (subsectionIndex) =>
        colorPalette[subsectionIndex % colorPalette.length],
      getRowConfigForTrack: (subsectionIndex, trackIndex) =>
        rowConfigurations.find(
          (config) =>
            config.subsectionIndex === subsectionIndex &&
            config.trackIndex === trackIndex,
        ),
      getColumnPosition: (columnKey) =>
        columnPositions[columnKey] || { start: 0, size: 0, end: 0 },
    },
  };
};

export default assembleChainVisualizationConfig;
